import { createSelector } from '@reduxjs/toolkit';
import { orderBy } from 'lodash-es';
import { selectSelectedProjectId } from 'store/projects/selectors/projectsSelectors';
import { getFirestopImageTypeOrder } from '../constants/firestopImageTypes';

const selectFirestopDocumentationState = (state) => state.firestopDocumentation;

export const selectFirestopListsEntities = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopLists.entities
);

export const selectFirestopItemsEntities = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopItems.entities
);

export const selectFirestopImagesEntities = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopImages.entities
);

export const selectFirestopImagesArray = createSelector(
  [selectFirestopImagesEntities],
  (entities) => Object.values(entities)
);

export const selectFirestopListsArray = createSelector(
  [selectFirestopListsEntities],
  (entities) => Object.values(entities)
);

export const selectFirestopItemsArray = createSelector(
  [selectFirestopItemsEntities],
  (entities) => Object.values(entities)
);

export const selectSelectedProjectFirestopLists = createSelector(
  [selectFirestopListsArray, selectSelectedProjectId],
  (firestopLists, selectedProjectId) =>
    firestopLists.filter(
      (firestopList) =>
        firestopList.projectId === selectedProjectId && !firestopList.deletedAt
    )
);

export const selectNotDeletedFirestopItemsArray = createSelector(
  [selectFirestopItemsArray],
  (firestopItems) =>
    firestopItems.filter((firestopItem) => !firestopItem.deletedAt)
);

export const selectSelectedProjectFirestopItems = createSelector(
  [selectNotDeletedFirestopItemsArray, selectSelectedProjectId],
  (firestopItems, selectedProjectId) =>
    firestopItems.filter(
      (firestopItem) => firestopItem.projectId === selectedProjectId
    )
);

export const selectFirestopItemsToCreate = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopItemsToCreate
);

export const selectFirestopImagesToAdd = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopImagesToAdd
);

export const selectIsFirestopDocumentationDataLoading = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.loading
);

export const selectIsCreateFirestopListDialogOpen = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isCreateFirestopListDialogOpen
);

export const selectSelectedFirestopListId = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.selectedFirestopListId
);

export const selectIsEditingListAttributes = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isEditingListAttributes
);

export const selectFirestopListAttributeModifications = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopListAttributeModifications
);

export const selectIsAddNewFirestopDialogOpen = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isAddNewFirestopDialogOpen
);

export const selectIsCreatingOrUpdatingFirestopData = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isCreatingOrUpdatingFirestopData
);

export const selectSelectedFirestopItemIdToDelete = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.selectedFirestopItemIdToDelete
);

export const selectSelectedFirestopItemIdToEdit = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.selectedFirestopItemIdToEdit
);

export const selectEditedFirestopAttributes = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.editedFirestopAttributes
);

export const selectFirestopEditImagesToDelete = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopEditImagesToDelete
);

export const selectSelectedAdditionFirestopItemId = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.selectedAdditionFirestopItemId
);

export const selectFirestopAdditionAttributes = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopAdditionAttributes
);

export const selectFirestopImageDescriptions = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.imageDescriptions
);

export const selectLatestWorkedAtByResource = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.latestWorkedAtByResource
);

export const selectIsPrintingFirestopListPdf = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isPrintingFirestopListPdf
);

export const selectIsConfirmDeleteFirestopListDialogOpen = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isConfirmDeleteFirestopListDialogOpen
);

export const selectIsDeletingFirestopList = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isDeletingFirestopList
);

export const selectSelectedFirestopList = createSelector(
  [selectFirestopListsEntities, selectSelectedFirestopListId],
  (firestopListEntities, selectedFirestopListId) =>
    firestopListEntities[selectedFirestopListId]
);

const selectSelectedFirestopListFirestopItems = createSelector(
  [selectFirestopItemsArray, selectSelectedFirestopListId],
  (firestopItems, selectedFirestopListId) =>
    firestopItems.filter(
      (firestopItem) => firestopItem.listId === selectedFirestopListId
    )
);

export const selectSelectedFirestopListWithModifications = createSelector(
  [selectSelectedFirestopList, selectFirestopListAttributeModifications],
  (firestopList, firestopListModifications) => ({
    ...firestopList,
    ...firestopListModifications,
  })
);

export const selectSelectedFirestopListData = createSelector(
  [selectSelectedFirestopListFirestopItems],
  (listFirestopItems) => {
    const inProgressFirestopItems = [];
    const completedFirestopItems = [];

    listFirestopItems.forEach((firestopItem) => {
      if (firestopItem.status === 'COMPLETED') {
        completedFirestopItems.push(firestopItem);
      } else {
        inProgressFirestopItems.push(firestopItem);
      }
    });

    const createdAtSortedInProgressFirestopItems = orderBy(
      inProgressFirestopItems,
      'createdAt',
      'desc'
    );

    const createdAtSortedCompletedFirestopItems = orderBy(
      completedFirestopItems,
      'createdAt',
      'desc'
    );

    const sortedFirestopItems = [
      ...createdAtSortedInProgressFirestopItems,
      ...createdAtSortedCompletedFirestopItems,
    ];

    return {
      firestopItems: sortedFirestopItems,
    };
  }
);

export const selectFirestopSummaryData = createSelector(
  [selectSelectedProjectFirestopLists, selectSelectedProjectFirestopItems],
  (firestopLists, firestopItems) => {
    const firestopListsAmount = firestopLists.length;
    const firestopItemsAmount = firestopItems.length;

    const completedFirestopItems = [];
    const inProgressFirestopItems = [];

    firestopItems.forEach((firestopItem) => {
      if (firestopItem.status === 'COMPLETED') {
        completedFirestopItems.push(firestopItem);
      } else {
        inProgressFirestopItems.push(firestopItem);
      }
    });

    const totalCompletedFirestops = completedFirestopItems.length;
    const totalInProgressFirestops = inProgressFirestopItems.length;

    return {
      firestopListsAmount,
      firestopItemsAmount,
      totalCompletedFirestops,
      totalInProgressFirestops,
    };
  }
);

export const selectSelectedFirestopItemToEdit = createSelector(
  [selectFirestopItemsEntities, selectSelectedFirestopItemIdToEdit],
  (firestopItems, selectedFirestopItemId) =>
    firestopItems[selectedFirestopItemId]
);

export const selectSelectedProjectFirestopImages = createSelector(
  [selectFirestopImagesArray, selectSelectedProjectId],
  (firestopImages, selectedProjectId) =>
    firestopImages.filter(
      (firestopImage) => firestopImage.projectId === selectedProjectId
    )
);

export const selectFirestopImagesByFirestopItemId = createSelector(
  [selectSelectedProjectFirestopImages, (_, args) => args],
  (firestopImages, args) => {
    const thisFirestopImages = firestopImages.filter(
      (firestopImage) => firestopImage.firestopItemId === args
    );

    // Blueprint/location images come first (ordered by their image type), then
    // the actual firestop photos. Within each group the original
    // capture/add order is kept via createdAt.
    const sortedImages = orderBy(
      thisFirestopImages,
      [(firestopImage) => getFirestopImageTypeOrder(firestopImage), 'createdAt'],
      ['asc', 'asc']
    );

    return sortedImages;
  }
);

export const selectSelectedFirestopItemImages = createSelector(
  [
    selectFirestopImagesArray,
    selectSelectedFirestopItemIdToEdit,
    selectFirestopEditImagesToDelete,
  ],
  (firestopImages, selectedFirestopItemId, imagesAddedForDeletion) =>
    firestopImages.filter(
      (firestopImage) =>
        firestopImage.firestopItemId === selectedFirestopItemId &&
        !imagesAddedForDeletion.includes(firestopImage.id)
    )
);

export const selectFirestopItemsCountTotalsByFirestopListId = createSelector(
  [selectSelectedProjectFirestopItems, (_, args) => args],
  (firestopItems, firestopListId) => {
    const listFirestopItems = firestopItems.filter(
      (firestopItem) => firestopItem.listId === firestopListId
    );

    const completedFirestopItems = [];
    const inProgressFirestopItems = [];

    listFirestopItems.forEach((firestopItem) => {
      if (firestopItem.status === 'COMPLETED') {
        completedFirestopItems.push(firestopItem);
      } else {
        inProgressFirestopItems.push(firestopItem);
      }
    });

    const firestopItemsAmount = listFirestopItems.length;
    const totalCompletedFirestops = completedFirestopItems.length;
    const totalInProgressFirestops = inProgressFirestopItems.length;

    return {
      firestopItemsAmount,
      totalCompletedFirestops,
      totalInProgressFirestops,
    };
  }
);
